import { createElement, useContext, useEffect, useRef, useState } from "react"
import classNames from "classnames"

import { ValueView } from "web/components"

import { DataTableContext } from "./DataTable"
import { useNavigateByKey } from "./useNavigateByKey"
import { LockIcon } from "web/components/ValueView"
import { useCurrentUser } from "web/hooks/data"
import { useModals } from "web/hooks/modals"

const SPACE_KEY = " "

function focusIsInModal() {
  return document.activeElement.closest(".modal-window") !== null
}

function RefocusWhenModalCloses({ cellRef }) {
  const { modals } = useModals()
  const modalWasOpen = useRef(false)

  useEffect(() => {
    if (modals.length > 0) {
      modalWasOpen.current = true
    } else if (modalWasOpen.current) {
      modalWasOpen.current = false

      if (document.activeElement === document.body) {
        cellRef.current.focus()
      }
    }
  }, [modals.length, cellRef])

  return null
}

export default function DataCell({ data, column, rowNumber, cell }) {
  const {
    ref: cellRef,
    tabIndex,
    isEditable: cellIsEditable,
    valueLocked,
  } = cell
  const {
    icon,
    type = "data",
    style: colStyle = {},
    dataKey,
    presentation,
  } = column

  const [inEditMode, setInEditMode] = useState(false)

  const currentUser = useCurrentUser()

  const {
    cells,
    onRowClick,
    renderToEditCell,
    isEditable: tableIsEditable,
    isCompact,
    openLockedFieldEditModal,
    isModalEditable,
    openEditModal,
  } = useContext(DataTableContext)

  const navigateByKey = useNavigateByKey({ cell, cells })

  function handleClick(e) {
    if (tableIsEditable) {
      e.stopPropagation()
    }

    if (!cellIsEditable) return

    if (cellRef.current === document.activeElement) {
      if (valueLocked && currentUser.is_data_admin) {
        openLockedFieldEditModal && openLockedFieldEditModal(dataKey, data)
      } else if (isModalEditable && isModalEditable(dataKey, data)) {
        openEditModal(dataKey, data)
      } else {
        setInEditMode(true)
      }
    }
  }

  function handleKeyDown(e) {
    const { key } = e

    if (navigateByKey(e)) return

    if (inEditMode) {
      // special condition to toggle checkboxes with space key
      if (key === SPACE_KEY) {
        const checkBoxInCell = e.target.querySelector('input[type="checkbox"]')
        if (checkBoxInCell) {
          checkBoxInCell.click()
          e.preventDefault()
        }
      }

      if (key === "Escape") {
        setInEditMode(false)
      }
      return
    }

    if (key === "Enter") {
      if (valueLocked && currentUser.is_data_admin) {
        openLockedFieldEditModal && openLockedFieldEditModal(dataKey, data)
        return
      }

      if (isModalEditable(dataKey, data)) {
        openEditModal(dataKey, data)
        return
      }

      // special condition to trigger click on enter
      const element = e.target.querySelector(
        ".edit-mode-and-click-on-enter-key"
      )
      if (element) {
        element.click()
        return
      }

      setInEditMode(true)
    }

    if (key === "Escape") {
      cellRef.current.blur()
    }
  }

  function handleBlur(e) {
    // take focus when child blurs if nothing else was focused in the meantime
    if (cellRef.current.contains(e.target) && cellRef.current !== e.target) {
      setTimeout(() => {
        const nothingInFocus = document.activeElement === document.body

        if (nothingInFocus) {
          cellRef.current.focus()
        }
      }, 1)
    }

    // leave edit mode on blur, unless focus moved into a modal opened from the cell
    setTimeout(() => {
      if (cellRef.current.contains(document.activeElement)) return
      if (focusIsInModal()) return

      setInEditMode(false)
    }, 1)
  }

  // need to apply some of row styles here because row is display:contents
  const rowIsEven = rowNumber % 2 === 0

  const usePointerCursor = typeof onRowClick === "function" || cellIsEditable

  const cellClasses = classNames(
    "datatable-data-cell border-b max-w-full flex focus:bg-primary-10 focus:outline-none border-neutral-9",
    {
      "h-9": isCompact,
      "h-10": !isCompact,
      "cursor-pointer": usePointerCursor,
      "cursor-text": tableIsEditable && cellIsEditable,
      "cursor-not-allowed": tableIsEditable && !cellIsEditable,
      "text-neutral-7": type === "data" && tableIsEditable && !cellIsEditable,
      "px-0 z-10": inEditMode,
      "px-2 py-1": !inEditMode,
      "bg-white": tableIsEditable || !rowIsEven,
      "bg-primary-11": !tableIsEditable && rowIsEven,
      "border-r": tableIsEditable || column.isLastFrozenColumn,
      "border-r-neutral-9": column.isLastFrozenColumn,
      "text-primary-4": data.sticky,
      "font-medium": !tableIsEditable && data.sticky,
      sticky: column.freeze,
    }
  )

  const cellStyle = { ...colStyle, left: column.leftPosition }

  const showLock = valueLocked && tableIsEditable

  return (
    <div
      ref={cellRef}
      data-testid={`datacell-${rowNumber}-${column.dataKey}`}
      tabIndex={tabIndex}
      className={cellClasses}
      style={cellStyle}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onBlur={handleBlur}
    >
      {inEditMode && <RefocusWhenModalCloses cellRef={cellRef} />}
      {(type === "data" || type === undefined) &&
        (inEditMode ? (
          renderToEditCell({ rowData: data, column })
        ) : (
          <span className="flex w-full items-center">
            <ValueView
              fieldKey={dataKey}
              fieldPresentation={presentation}
              readOnly={!cell.isEditable || valueLocked}
              percentValue={data.values[dataKey]?.percent_value}
              preventClickHandler={
                tableIsEditable && valueLocked && currentUser.is_data_admin
              }
              {...data.values[dataKey]}
            />
            {showLock && (
              <span className="ml-auto">
                <LockIcon />
              </span>
            )}
          </span>
        ))}
      {type === "icon" && (
        <img
          src={typeof icon === "function" ? icon(data) : icon}
          width="20px"
          alt="cell icon"
        />
      )}
      {type === "text" && column.text}
      {type === "component" && createElement(column.component, { data })}
    </div>
  )
}
